import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Accordion,
  AccordionDetails,
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useChannel } from 'ably/react';
import moment from 'moment';

import { NCICApi } from '@/api';
import { IcArrowDown, IcArrowUp, IcNoRecords } from '@/assets/images';
import {
  ConfirmationDialog,
  FormCard,
  FormRoot,
  Page,
  PageHeader,
  ViewItem,
} from '@/components';
import { DATE_TIME_FORMAT } from '@/config';
import { withLoader, WithLoaderProps } from '@/hocs';
import { useUserStore } from '@/store';
import { formatPersonName, getItemValueFromObject } from '@/utils';

import { NCICRequestRecord, NCICViewFields, Response } from '../data';
import {
  NCICHistoryDetails,
  NCICHistoryList,
  StyledAccordionSummary,
} from './styles';

const NCICSearchHistory: FC<WithLoaderProps> = ({ showLoader, hideLoader }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>(false);
  const theme = useTheme();
  const { account } = useUserStore();

  const handleChange =
    (panel: NCICRequestRecord) =>
    (_: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel.uniqueId : false);
      setSelectedQuery(newExpanded ? panel.uniqueId : null);
      setSelectedQueryData(newExpanded ? panel : null);
      setSelectedResult(null);
    };

  const [results, setResults] = useState<NCICRequestRecord[]>([]);
  const [selectedQuery, setSelectedQuery] = useState<any | null>(null);
  const [selectedQueryData, setSelectedQueryData] =
    useState<NCICRequestRecord | null>(null);
  const [selectedResult, setSelectedResult] = useState<Response | null>(null);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [showCriteria, setShowCriteria] = useState<boolean>(true);

  useChannel(`account:${account?._id}:ncic`, () => {
    fetchData(false);
  });

  const fetchData = async (showLoading = true) => {
    if (showLoading) {
      showLoader();
    }
    const res = await NCICApi.getHistory();
    setResults(
      res.data
        .filter((item: NCICRequestRecord) => {
          return item.createdAt;
        })
        .sort((a: NCICRequestRecord, b: NCICRequestRecord) => {
          return (
            new Date(b.createdAt || b.updatedAt).getTime() -
            new Date(a.createdAt || a.updatedAt).getTime()
          );
        }),
    );
    hideLoader();
  };

  // const handleDelete = (response: any) => {
  //   logger.info('Delete response', response);
  //   setShowConfirm(true);
  // };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Page title="NCIC Search Results">
      <PageHeader
        title="NCIC Search Results"
        onBack={() => navigate('/ncic-search')}
      >
        <Button
          onClick={() => {
            navigate('/ncic-search');
          }}
          color="error"
          variant="contained"
          size="medium"
        >
          New Search
        </Button>
      </PageHeader>
      <Stack
        gap={2}
        p={1}
        direction="row"
        justifyContent="space-between"
        height="100%"
        flex={1}
      >
        {results.length === 0 ? (
          <Stack
            flex={1}
            gap={2}
            sx={{
              alignItems: 'center',
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <IcNoRecords color={theme.palette.text.disabled} />
            <Typography fontWeight={500} fontSize={24}>
              No results found
            </Typography>
          </Stack>
        ) : (
          <>
            <NCICHistoryList>
              {results.map((result, index) => (
                <Accordion
                  key={index}
                  expanded={expanded === result.uniqueId}
                  onChange={handleChange(result)}
                  sx={{ boxShadow: 'none' }}
                >
                  <StyledAccordionSummary
                    expandIcon={
                      <IcArrowDown color={theme.palette.error.main} />
                    }
                    aria-controls={`panel${index}d-content`}
                    id={`panel${index}d-header`}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      flex={1}
                    >
                      <Typography component="span">
                        {moment(result.updatedAt || result.createdAt).format(
                          DATE_TIME_FORMAT,
                        )}{' '}
                        -{' '}
                        {result.request.firstName || result.request.lastName
                          ? formatPersonName(result.request)
                          : `NCIC Search`}
                      </Typography>
                      {/* <Tooltip title="Delete">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(result);
                      }}
                    >
                      <IcDeleteTrash color={theme.palette.error.main} />
                    </IconButton>
                  </Tooltip> */}
                    </Stack>
                  </StyledAccordionSummary>
                  <AccordionDetails
                    sx={{ backgroundColor: ' rgba(238, 62, 92, 0.05)' }}
                  >
                    {result.response?.length === 0 ? (
                      <Box
                        sx={{
                          p: 2,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Typography>No results found</Typography>
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table aria-label="simple table">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ p: 1 }}>From</TableCell>
                              <TableCell sx={{ p: 1 }}>Subject</TableCell>
                              <TableCell sx={{ p: 1 }}>Date and Time</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {result.response?.map((row, i) => (
                              <TableRow
                                key={row['api:Transaction'].id || i}
                                sx={
                                  row === selectedResult
                                    ? {
                                        background: 'rgba(238, 62, 92, 0.4)',
                                      }
                                    : undefined
                                }
                                onClick={() => {
                                  setSelectedResult(row);
                                }}
                              >
                                <TableCell sx={{ p: 1 }}>ConnectCiC</TableCell>
                                <TableCell sx={{ p: 1 }}>
                                  {
                                    row['api:Transaction']
                                      .LawEnforcementTransaction.Transaction
                                      .Response.MessageType
                                  }
                                </TableCell>
                                <TableCell sx={{ p: 1 }}>
                                  {moment(
                                    result.updatedAt || result.createdAt,
                                  ).format(DATE_TIME_FORMAT)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </NCICHistoryList>
            <NCICHistoryDetails>
              {selectedQuery ? (
                <>
                  <Tooltip title="You can hide/show search criteria">
                    <ButtonBase
                      onClick={() => setShowCriteria(!showCriteria)}
                      disableRipple
                      sx={{ width: '100%', borderRadius: 0 }}
                    >
                      <Stack
                        padding={2}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flex={1}
                      >
                        <Typography fontWeight={500} fontSize={16}>
                          Search Criteria
                        </Typography>

                        {!showCriteria ? <IcArrowDown /> : <IcArrowUp />}
                      </Stack>
                    </ButtonBase>
                  </Tooltip>
                  <Collapse in={showCriteria}>
                    <FormRoot>
                      <Grid container spacing={2}>
                        {selectedQueryData?.request &&
                          NCICViewFields.map(({ items, size }, i) => {
                            return (
                              <Grid item key={i} {...size}>
                                <FormCard>
                                  {items.map((item) => (
                                    <ViewItem
                                      key={item.label}
                                      label={item.label}
                                      value={getItemValueFromObject(
                                        selectedQueryData?.request,
                                        item.value,
                                      )}
                                      md={2}
                                    />
                                  ))}
                                </FormCard>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </FormRoot>
                  </Collapse>
                  {!selectedQueryData?.response && (
                    <Box
                      sx={{
                        p: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Stack alignItems="center" gap={1}>
                        <CircularProgress />
                        <Typography>
                          Proccessing results from this search...
                        </Typography>
                        <Alert severity="info" title="Info">
                          This may take a few minutes depending on the size of
                          the search results. We will inform you when the
                          results are ready.
                        </Alert>
                      </Stack>
                    </Box>
                  )}
                  {selectedResult ? (
                    <Box
                      sx={{ p: 2, backgroundColor: 'rgba(238, 62, 92, 0.1)' }}
                    >
                      <Typography variant="h5" gutterBottom>
                        {selectedQueryData?.request.firstName ||
                        selectedQueryData?.request.lastName
                          ? formatPersonName(selectedQueryData?.request)
                          : `NCIC Search`}
                      </Typography>
                      <Typography
                        fontSize={14}
                        color="textSecondary"
                        gutterBottom
                      >
                        Request ID: {selectedResult['api:Transaction'].id}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2">
                        {
                          selectedResult['api:Transaction']
                            .LawEnforcementTransaction.Transaction.Response
                            .MessageData
                        }
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Stack alignItems="center" gap={1}>
                        {/* <CircularProgress /> */}
                        <Typography>
                          Please select a record to view details...
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </>
              ) : (
                <Stack
                  gap={2}
                  sx={{
                    alignItems: 'center',
                    height: '100%',
                    justifyContent: 'center',
                  }}
                >
                  <IcNoRecords color={theme.palette.text.disabled} />
                  <Typography fontWeight={500} fontSize={24}>
                    No record selected
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    color={theme.palette.text.disabled}
                  >
                    Please select a record to display the data
                  </Typography>
                </Stack>
              )}
            </NCICHistoryDetails>
          </>
        )}
      </Stack>
      <ConfirmationDialog
        title="Confirm Deletion"
        description="Are you sure you want to delete this search result? This action cannot be undone."
        confirmText="Delete"
        open={showConfirm}
        onCancel={() => {
          setShowConfirm(false);
        }}
        onConfirm={() => {
          setShowConfirm(false);
        }}
      />
    </Page>
  );
};

export default withLoader(NCICSearchHistory);
